import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { usersData$ } from "../../reduxStore/selectors/userSelectors";
import FeatureFlagToggle from "../featureFlagToggle/featureFlagToggle";
import { hasRole } from "../../utils/Enum/RoleEnum";
import { FEATURE_ACCESS } from "../../utils/roleAccess";
import styles from "./asset/sideBar.module.scss";

const IS_DEMO = process.env.REACT_APP_ENVCONFIG === 'demo';

const SideBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1380);
  const [showFeatureFlags, setShowFeatureFlags] = useState(false);
  const location = useLocation();
  const userData = useSelector(usersData$);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 1380;
      setIsMobile(mobile);
      if (!mobile) {
        setIsOpen(false); // Close sidebar when switching to desktop
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar when route changes (mobile only)
  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    }
  }, [location.pathname, isMobile]);

  const isActive = (path) => {
    return location.pathname.includes(path);
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  return (
    <>
      {/* Mobile Toggle Button */}
      {isMobile && (
        <button
          className={styles.mobileToggle}
          onClick={toggleSidebar}
          aria-label="Toggle navigation menu"
        >
          {isOpen ? '✕' : '☰'}
        </button>
      )}

      {/* Overlay for mobile */}
      {isMobile && (
        <div
          className={`${styles.overlay} ${isOpen ? styles.show : ''}`}
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`${styles.sideBar} ${isOpen ? styles.open : ''}`}>
        <div className={styles.logoContainer}>
          <img
            src={require("../../publicAsset/media/Prepal Logo 3.svg").default}
            alt="Prepal Logo"
            className={styles.logo}
          />
        </div>

        {/* Menu Items */}
        <nav className={styles.menuItems}>
          <Link
            to="/user-dashboard"
            className={`${styles.menuItem} ${isActive("/user-dashboard") ? styles.active : ""
              }`}
            onClick={isMobile ? closeSidebar : undefined}
          >
            <div className={styles.menuIcon}>
              <img
                src={require("../../publicAsset/media/NavIconDashboard.svg").default}
                alt="Dashboard"
              />
            </div>
            <span>Dashboard</span>
          </Link>

          <Link
            to="/user-sat-practice"
            className={`${styles.menuItem} ${isActive("/user-sat-practice") || isActive("/user-practice-session") ? styles.active : ""
              }`}
            onClick={isMobile ? closeSidebar : undefined}
          >
            <div className={styles.menuIcon}>
              <img
                src={require("../../publicAsset/media/NavIconSATPrep.svg").default}
                alt="SAT Prep"
              />
            </div>
            <span>Practice Mode</span>
          </Link>

          {!IS_DEMO && (
            <Link
              to="/user-setting"
              className={`${styles.menuItem} ${isActive("/user-setting") ? styles.active : ""
                }`}
              onClick={isMobile ? closeSidebar : undefined}
            >
              <div className={styles.menuIcon}>
                <img
                  src={require("../../publicAsset/media/NavIconSettings.svg").default}
                  alt="Settings"
                />
              </div>
              <span>Profile</span>
            </Link>
          )}

          {/* Admin-only Feature Flag Toggle */}
          {hasRole(userData, FEATURE_ACCESS.FEATURE_FLAGS) && (
            <button
              className={styles.featureFlagButton}
              onClick={() => setShowFeatureFlags(true)}
              title="Feature Flags (Admin Only)"
            >
              <div className={styles.menuIcon}>
                <span style={{ fontSize: '1.5rem' }}>🚩</span>
              </div>
              <span>Feature Flags</span>
            </button>
          )}
        </nav>
      </div>

      {/* Feature Flag Toggle Modal */}
      <FeatureFlagToggle
        isOpen={showFeatureFlags}
        onClose={() => setShowFeatureFlags(false)}
      />
    </>
  );
};

export default SideBar;
